import type { Attendance } from "@book-club/shared";
import { deleteField } from "firebase/firestore";
import firebaseDatabaseService from "@/infrastructure/firebase/firebaseDatabaseService";
import logger from "@/logger";

/**
 * Mark attendance as attended
 * Sets attendedAt, resolvedAt, and clears wasAbsent
 */
async function markAttendanceAsAttended(attendanceId: string): Promise<void> {
    const now = new Date();
    await firebaseDatabaseService.updateDocument<Attendance>("attendance", attendanceId, {
        attendedAt: now,
        cancelledAt: deleteField(),
        resolvedAt: now,
        wasAbsent: false,
    });
    logger.debug(`[Attendance] Marked ${attendanceId} as attended`);
}

/**
 * Mark attendance as absent (no show)
 * Sets wasAbsent to true, attendedAt, and resolvedAt
 */
async function markAttendanceAsAbsent(attendanceId: string): Promise<void> {
    const now = new Date();
    await firebaseDatabaseService.updateDocument<Attendance>("attendance", attendanceId, {
        attendedAt: deleteField(),
        resolvedAt: now,
        tableNumber: deleteField(),
        wasAbsent: true,
    });
    logger.debug(`[Attendance] Marked ${attendanceId} as absent`);
}

/**
 * Mark attendance as cancelled
 * Sets cancelledAt, resolvedAt, and clears attendedAt, tableNumber, and wasAbsent
 */
async function markAttendanceAsCancelled(attendanceId: string): Promise<void> {
    const now = new Date();
    await firebaseDatabaseService.updateDocument<Attendance>("attendance", attendanceId, {
        attendedAt: deleteField(),
        cancelledAt: now,
        resolvedAt: now,
        tableNumber: deleteField(),
        wasAbsent: false,
    });
    logger.debug(`[Attendance] Marked ${attendanceId} as cancelled`);
}

/**
 * Undo live check-in: clear attendedAt and resolvedAt so the booking is pending again.
 * Preserves tableNumber (seating intent) and does not cancel the booking.
 */
async function clearCheckIn(attendanceId: string): Promise<void> {
    await firebaseDatabaseService.updateDocument<Attendance>("attendance", attendanceId, {
        attendedAt: deleteField(),
        resolvedAt: deleteField(),
    });
    logger.debug(`[Attendance] Cleared check-in for ${attendanceId}`);
}

async function updateTableNumber(attendanceId: string, tableNumber: number | null): Promise<void> {
    if (tableNumber == null) {
        await firebaseDatabaseService.updateDocument<Attendance>("attendance", attendanceId, {
            tableNumber: deleteField(),
        });
        logger.debug(`[Attendance] Cleared tableNumber for ${attendanceId}`);
        return;
    }

    await firebaseDatabaseService.updateDocument<Attendance>("attendance", attendanceId, {
        tableNumber,
    });
    logger.debug(`[Attendance] Updated ${attendanceId} to table ${tableNumber}`);
}

const attendanceService = {
    clearCheckIn,
    markAttendanceAsAbsent,
    markAttendanceAsAttended,
    markAttendanceAsCancelled,
    updateTableNumber,
};

export default attendanceService;
